import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send, Loader2, Mic, Square } from 'lucide-react';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { AudioWaveform } from '@/components/oreja/AudioWaveform';
import { uploadVoiceBlob, transcribeAudio } from '@/lib/oreja/session-helpers';

interface ChatInputProps {
  onSend: (message: string) => Promise<unknown>;
  disabled?: boolean;
  isSending?: boolean;
  placeholder?: string;
}

/**
 * Formats a duration in seconds as M:SS (e.g. 65 → "1:05").
 *
 * @param seconds - Elapsed seconds.
 * @returns Formatted duration string.
 */
function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * ChatInput - Textarea + send button + voice input for the Ask Poco chat panel.
 *
 * Users can type a message and send with Enter or the send button.
 * They can also click the microphone button to record a voice message which
 * is transcribed server-side and appended to the textarea for review before
 * sending.
 *
 * @param onSend - Async callback invoked with the trimmed message text.
 * @param disabled - Externally disable all controls.
 * @param isSending - True while the parent is waiting for the AI response.
 * @param placeholder - Textarea placeholder text.
 */
export function ChatInput({
  onSend,
  disabled,
  isSending,
  placeholder = 'Ask about this client...',
}: ChatInputProps) {
  const [message, setMessage] = useState('');
  const [localSending, setLocalSending] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const transcriptionTriggeredRef = useRef(false);

  const recorder = useAudioRecorder({ maxDurationSec: 120, warnAtSec: 90 });
  const {
    status,
    audioBlob,
    duration,
    stream,
    error: recorderError,
    startRecording,
    stopRecording,
    reset,
  } = recorder;

  const isRecording = status === 'recording' || status === 'requesting';
  const isVoiceBusy = isRecording || isTranscribing;

  // Surface mic permission / hardware errors from the recorder
  const displayError = voiceError || (status === 'error' ? recorderError : null);

  // Textarea and send-flow are disabled while voice is active or sending
  const isDisabled = disabled || isSending || localSending || isVoiceBusy;

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, [message]);

  /**
   * Runs the upload → transcribe pipeline when the recorder stops with a blob.
   * Appends the transcript to whatever is already in the textarea.
   */
  const handleTranscription = useCallback(
    async (blob: Blob) => {
      setIsTranscribing(true);
      setVoiceError(null);
      try {
        const sessionId = `chat-${Date.now()}`;
        const audioPath = await uploadVoiceBlob(sessionId, blob, 'chat-input');
        const transcript = await transcribeAudio(audioPath);
        setMessage((prev) => (prev ? `${prev} ${transcript}` : transcript));
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Transcription failed';
        setVoiceError(msg);
      } finally {
        setIsTranscribing(false);
        reset();
      }
    },
    [reset],
  );

  // Trigger transcription exactly once when recorder reaches 'stopped' state with a blob
  useEffect(() => {
    if (status === 'stopped' && audioBlob && !transcriptionTriggeredRef.current) {
      transcriptionTriggeredRef.current = true;
      void handleTranscription(audioBlob);
    }
    if (status === 'idle') {
      transcriptionTriggeredRef.current = false;
    }
  }, [status, audioBlob, handleTranscription]);

  const handleSubmit = async () => {
    const trimmed = message.trim();
    if (!trimmed || isDisabled) return;

    setLocalSending(true);
    try {
      await onSend(trimmed);
      setMessage('');
    } catch {
      // Error is handled upstream (toast + inline error).
      // Preserve user's message so they can retry.
    } finally {
      setLocalSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  const handleMicClick = () => {
    setVoiceError(null);
    if (status === 'error') {
      reset(); // Clear recorder error before retrying
    }
    if (isRecording) {
      stopRecording();
    } else {
      void startRecording();
    }
  };

  return (
    <div className="border-t bg-background p-4">
      {/* Recording / transcribing status indicator */}
      {(isRecording || isTranscribing) && (
        <div className="flex items-center gap-2 mb-2 text-sm">
          {isRecording ? (
            <>
              <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse shrink-0" />
              <span className="text-red-600 font-medium shrink-0">
                {formatDuration(duration)}
              </span>
              <AudioWaveform
                stream={stream}
                width={160}
                height={28}
                barWidth={2}
                gap={1}
                barColor="hsl(0, 84%, 60%)"
              />
            </>
          ) : (
            <>
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground shrink-0" />
              <span className="text-muted-foreground">Transcribing...</span>
            </>
          )}
        </div>
      )}

      {/* Voice error message */}
      {displayError && (
        <p className="text-xs text-destructive mb-2">{displayError}</p>
      )}

      <div className="flex gap-2">
        <Textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isDisabled}
          rows={1}
          className="min-h-[44px] max-h-[200px] resize-none"
        />

        {/* Microphone / stop button */}
        <Button
          onClick={handleMicClick}
          disabled={isTranscribing}
          variant={isRecording ? 'destructive' : 'outline'}
          size="icon"
          className="shrink-0 h-[44px] w-[44px]"
          aria-label={isRecording ? 'Stop recording' : 'Voice input'}
        >
          {isTranscribing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isRecording ? (
            <Square className="h-4 w-4" />
          ) : (
            <Mic className="h-4 w-4" />
          )}
        </Button>

        {/* Send button */}
        <Button
          onClick={() => void handleSubmit()}
          disabled={!message.trim() || isDisabled}
          size="icon"
          className="shrink-0 h-[44px] w-[44px]"
          aria-label="Send"
        >
          {isSending || localSending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-2 hidden sm:block">
        Press Enter to send, Shift+Enter for new line
      </p>
    </div>
  );
}
